import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import { Button, Field, Modal } from '@ecom/ui-kit';
import { type ReactElement, type ReactNode, useState } from 'react';

import { ConfirmPasswordField } from '@/components/ui/ConfirmPasswordField';

/**
 * A critical action in one dialog: a stated reason (10 characters at least, audited) AND the caller's own password again, as for every
 * state-changing action of this app. `onConfirm` must REJECT on failure: the refusal is told in words inside the dialog and the
 * password field is emptied, so a refused attempt leaves no password lingering in the DOM.
 */
export function PasswordActionDialog({
  open,
  title,
  warning,
  confirmLabel,
  subject,
  onClose,
  onConfirm,
  children,
  extraValid = true,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly warning?: string;
  readonly confirmLabel: string;
  /** Who the action is about (shown above the fields). */
  readonly subject?: string;
  readonly onClose: () => void;
  readonly onConfirm: (input: { reason: string; password: string }) => Promise<void>;
  /** Extra fields (a verification method...) shown between the warning and the reason. */
  readonly children?: ReactNode;
  /** False while an extra field is still unanswered. */
  readonly extraValid?: boolean;
}): ReactElement {
  const { t } = useI18n();
  const [reason, setReason] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  function close(): void {
    setReason('');
    setPassword('');
    setError(null);
    onClose();
  }

  async function confirm(): Promise<void> {
    setError(null);
    setPending(true);
    try {
      await onConfirm({ reason, password });
      setReason('');
      setPassword('');
    } catch (failure) {
      setError(t(resolveErrorMessageKey(failure)));
      setPassword('');
    } finally {
      setPending(false);
    }
  }

  const canSubmit = extraValid && reason.trim().length >= 10 && password.length > 0;

  return (
    <Modal
      open={open}
      title={title}
      onClose={close}
      footer={
        <>
          <Button variant="secondary" onClick={close}>
            {t('merchant.common.cancel')}
          </Button>
          <Button
            onClick={() => {
              void confirm();
            }}
            loading={pending}
            disabled={!canSubmit}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {error !== null ? (
        <p role="alert" className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical">
          {error}
        </p>
      ) : null}
      {subject !== undefined ? <p className="mb-3 text-sm text-ink">{subject}</p> : null}
      {warning !== undefined ? (
        <p className="mb-3 rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">{warning}</p>
      ) : null}

      <div className="flex flex-col gap-4">
        {children}
        <Field label={t('admin.common.reasonLabel')}>
          {(bind) => (
            <textarea
              {...bind}
              value={reason}
              onChange={(event) => {
                setReason(event.target.value);
              }}
              rows={3}
              maxLength={500}
              className="ecom-input"
            />
          )}
        </Field>
        <ConfirmPasswordField value={password} onChange={setPassword} />
      </div>
    </Modal>
  );
}
